import { createZodDto } from "nestjs-zod/dto";
import { z } from "zod";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  PROVISIONING DE COMPTES — import CSV de comptes provisionnés (admin)
 * ─────────────────────────────────────────────────────────────────────────────
 *  Un compte provisionné = User réel sans mot de passe + invitation à usage
 *  unique (lien d'activation, expiry 30 j). L'import crée les comptes et
 *  planifie l'envoi des invitations via BullMQ.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export const PROVISION_BATCH_STATUSES = [
  "processing",
  "ready",
  "inviting",
  "done",
  "failed",
] as const;
export type ProvisionBatchStatus = (typeof PROVISION_BATCH_STATUSES)[number];

export const provisionBatchFilterSchema = z.object({
  search: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  cursor: z.string().optional(),
});
export class ProvisionBatchFilterDto extends createZodDto(provisionBatchFilterSchema) {}

/** Lignes d'un lot (comptes + état d'invitation). */
export const provisionRowFilterSchema = z.object({
  /** all | claimed | unclaimed */
  claimed: z.enum(["all", "claimed", "unclaimed"]).default("all"),
  search: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  cursor: z.string().optional(),
});
export class ProvisionRowFilterDto extends createZodDto(provisionRowFilterSchema) {}

export const resendInvitesSchema = z.object({
  /** Recontacter uniquement les comptes jamais réclamés (défaut). */
  onlyUnclaimed: z.boolean().default(true),
  /** Uniquement les invitations expirées. */
  onlyExpired: z.boolean().default(false),
});
export class ResendInvitesDto extends createZodDto(resendInvitesSchema) {}

/** Lot exposé (lecture). */
export const provisionBatchSchema = z.object({
  id: z.string(),
  name: z.string(),
  fileName: z.string().nullable().optional(),
  source: z.string(),
  status: z.string(),
  totalRows: z.number().int().min(0),
  createdCount: z.number().int().min(0),
  existingCount: z.number().int().min(0),
  invalidCount: z.number().int().min(0),
  invitedCount: z.number().int().min(0),
  claimedCount: z.number().int().min(0),
  failedCount: z.number().int().min(0),
  lastError: z.string().nullable().optional(),
  createdById: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type ProvisionBatchDto = z.infer<typeof provisionBatchSchema>;

export const provisionRowSchema = z.object({
  userId: z.string(),
  name: z.string().nullable().optional(),
  email: z.string(),
  emailVerified: z.boolean(),
  invitationSentAt: z.coerce.date().nullable().optional(),
  invitationExpiresAt: z.coerce.date().nullable().optional(),
  invitationAcceptedAt: z.coerce.date().nullable().optional(),
  invitationSendError: z.string().nullable().optional(),
});
export type ProvisionRowDto = z.infer<typeof provisionRowSchema>;

export const provisionBatchDetailSchema = provisionBatchSchema.extend({
  items: z.array(provisionRowSchema),
  nextCursor: z.string().nullable(),
});
export class ProvisionBatchDetailDto extends createZodDto(provisionBatchDetailSchema) {}

export const paginatedProvisionBatchesSchema = z.object({
  items: z.array(provisionBatchSchema),
  nextCursor: z.string().nullable(),
});
export class PaginatedProvisionBatchesDto extends createZodDto(paginatedProvisionBatchesSchema) {}

/** Résultat d'import. */
export const provisionImportResultSchema = z.object({
  batchId: z.string(),
  totalRows: z.number().int().min(0),
  created: z.number().int().min(0),
  existing: z.number().int().min(0),
  invalid: z.number().int().min(0),
  internalSkipped: z.number().int().min(0),
  invitationsQueued: z.number().int().min(0),
  errors: z.array(
    z.object({
      line: z.number().int(),
      email: z.string().nullable(),
      reason: z.string(),
    }),
  ),
});
export class ProvisionImportResultDto extends createZodDto(provisionImportResultSchema) {}
